import type { EngineeringFinding } from "@insights/contracts";
import { Badge, Button, Card } from "@insights/ui";
import {
  ArchiveRestore,
  Check,
  ExternalLink,
  SearchCheck,
  X,
} from "lucide-react";

const kindLabel: Record<EngineeringFinding["kind"], string> = {
  "provenance-gap": "Provenance gap",
  "high-churn": "High churn",
  "maintenance-risk": "Maintenance risk",
  "ownership-concentration": "Knowledge concentration",
  "review-bottleneck": "Review bottleneck",
  "removal-candidate": "Removal investigation",
};

function tone(value: EngineeringFinding["severity"]) {
  if (value === "high") return "danger" as const;
  if (value === "medium") return "warning" as const;
  return "neutral" as const;
}

export function FindingCard({
  finding,
  rank,
  onAcknowledge,
  onDismiss,
  onEvidenceOpen,
  onResolve,
  busy,
}: {
  finding: EngineeringFinding;
  rank: number;
  onAcknowledge: () => void;
  onDismiss: () => void;
  onEvidenceOpen: () => void;
  onResolve: () => void;
  busy: boolean;
}) {
  return (
    <Card as="section" className="finding-card">
      <header>
        <span className="finding-rank" aria-label={`Priority ${rank}`}>
          {rank}
        </span>
        <div>
          <div className="finding-badges">
            <Badge tone={tone(finding.severity)}>
              {finding.severity} priority
            </Badge>
            <Badge>{finding.confidence} confidence</Badge>
            <Badge>{kindLabel[finding.kind]}</Badge>
            {finding.kind === "provenance-gap" && finding.state !== "resolved" && finding.state !== "dismissed" && <Badge tone="danger">Needs attention</Badge>}
          </div>
          <h2>{finding.title}</h2>
          <p>{finding.repository} · {finding.scopeLabel}</p>
        </div>
      </header>

      <p className="finding-explanation">{finding.explanation}</p>
      <div className="finding-investigation">
        <SearchCheck aria-hidden="true" />
        <div>
          <strong>Suggested investigation</strong>
          <p>{finding.suggestedInvestigation}</p>
        </div>
      </div>

      <div
        className="finding-evidence"
        aria-label={`Evidence for ${finding.title}`}
      >
        {finding.evidence.map((item, index) => (
          <div key={`${item.label}:${index}`}>
            <span>{item.label}</span>
            {item.url ? (
              <a
                href={item.url}
                onClick={onEvidenceOpen}
                rel="noreferrer"
                target="_blank"
              >
                {item.value}
                <ExternalLink aria-hidden="true" />
              </a>
            ) : (
              <strong>{item.value}</strong>
            )}
            {item.detail && <small>{item.detail}</small>}
          </div>
        ))}
      </div>

      <footer>
        {finding.state !== "acknowledged" && (
          <Button
            color="neutral"
            disabled={busy}
            icon={<Check />}
            onClick={onAcknowledge}
            size="small"
          >
            Acknowledge
          </Button>
        )}
        {finding.state !== "resolved" && (
          <Button
            color="neutral"
            disabled={busy}
            icon={<ArchiveRestore />}
            onClick={onResolve}
            size="small"
          >
            Resolve
          </Button>
        )}
        {finding.state !== "dismissed" && (
          <Button
            color="neutral"
            disabled={busy}
            icon={<X />}
            onClick={onDismiss}
            size="small"
          >
            Dismiss
          </Button>
        )}
      </footer>
    </Card>
  );
}
